import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  ClipboardList,
  Code2,
  FileText,
  GraduationCap,
  Mic,
  Plus,
  Target,
  Trash2
} from "lucide-react";
import { getToken } from "../services/auth";
import { jwtDecode } from "jwt-decode";

const prepTools = [
  {
    title: "Resume Builder",
    desc: "Create a clean fresher resume with projects, skills, education, and achievements.",
    icon: FileText,
    color: "bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-300",
  },
  {
    title: "Mock Interviews",
    desc: "Practice HR, technical, and communication rounds before real interviews.",
    icon: Mic,
    color: "bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-300",
  },
  {
    title: "Coding Platform",
    desc: "Solve coding challenges and build confidence for technical rounds.",
    icon: Code2,
    color: "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300",
  },
  {
    title: "Aptitude Tests",
    desc: "Practice quantitative aptitude, reasoning, and verbal ability.",
    icon: ClipboardList,
    color: "bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-300",
  },
];

export default function PlacementPreparation() {
  const navigate = useNavigate();
  const [resume, setResume] = useState({
    name: "",
    role: "",
    skills: "",
    project: "",
  });
  const [interviews, setInterviews] = useState([
    { company: "TCS", role: "Graduate Trainee", status: "Applied" },
    { company: "Infosys", role: "System Engineer", status: "Mock Scheduled" },
  ]);
  const [newInterview, setNewInterview] = useState({ company: "", role: "", status: "Applied" });

  const user = useMemo(() => {
    try {
      const token = getToken();
      return token ? jwtDecode(token) : { role: "student" };
    } catch {
      return { role: "student" };
    }
  }, []);

  const filledResumeFields = Object.values(resume).filter(Boolean).length;
  const resumeProgress = Math.round((filledResumeFields / Object.keys(resume).length) * 100);

  const addInterview = () => {
    if (!newInterview.company.trim() || !newInterview.role.trim()) return;
    setInterviews(prev => [...prev, newInterview]);
    setNewInterview({ company: "", role: "", status: "Applied" });
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-950">
      
      <main className="flex-1 p-4 sm:p-6 lg:p-10 space-y-8 min-w-0">
        <section className="relative overflow-hidden rounded-lg bg-slate-950 px-5 py-8 text-white shadow-2xl shadow-slate-900/10 sm:px-8 lg:px-10">
          <div className="absolute right-0 top-0 h-56 w-56 translate-x-20 -translate-y-20 rounded-full bg-indigo-500/20 blur-3xl" />
          <div className="relative z-10 max-w-3xl">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-xs font-black uppercase tracking-widest text-indigo-100">
              <GraduationCap size={14} /> For Graduation Students
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-4xl">Placement Preparation</h1>
            <p className="mt-3 text-sm leading-6 text-slate-300 sm:text-base">
              Prepare for campus placements with resume building, mock interviews, coding practice, aptitude tests, and a simple interview tracker.
            </p>
          </div>
        </section>

        <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {prepTools.map((tool) => (
            <article key={tool.title} className="surface p-5">
              <div className={`mb-4 flex h-12 w-12 items-center justify-center rounded-lg ${tool.color}`}>
                <tool.icon size={23} />
              </div>
              <h2 className="text-lg font-black text-slate-950 dark:text-white">{tool.title}</h2>
              <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">{tool.desc}</p>
            </article>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-3">
          <div className="surface p-5 sm:p-6 xl:col-span-2">
            <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-black text-slate-950 dark:text-white">Resume Builder</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">Draft the essentials for a fresher resume.</p>
              </div>
              <div className="rounded-lg bg-indigo-50 px-3 py-2 text-sm font-black text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                {resumeProgress}% Complete
              </div>
            </div>

            <div className="mb-6 h-3 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
              <div className="h-full rounded-full bg-indigo-600 transition-all duration-700" style={{ width: `${resumeProgress}%` }} />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <input className="form-control" placeholder="Full name" value={resume.name} onChange={e => setResume(r => ({ ...r, name: e.target.value }))} />
              <input className="form-control" placeholder="Target role e.g. Frontend Developer" value={resume.role} onChange={e => setResume(r => ({ ...r, role: e.target.value }))} />
              <input className="form-control" placeholder="Top skills e.g. React, Python, SQL" value={resume.skills} onChange={e => setResume(r => ({ ...r, skills: e.target.value }))} />
              <input className="form-control" placeholder="Best project" value={resume.project} onChange={e => setResume(r => ({ ...r, project: e.target.value }))} />
            </div>

            <div className="mt-6 rounded-lg border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-950/60">
              <p className="text-xs font-black uppercase tracking-widest text-slate-400">Preview</p>
              <h3 className="mt-2 text-xl font-black text-slate-950 dark:text-white">{resume.name || "Your Name"}</h3>
              <p className="text-sm font-bold text-indigo-600 dark:text-indigo-300">{resume.role || "Target Role"}</p>
              <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">Skills: {resume.skills || "Add your strongest skills"}</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">Project: {resume.project || "Add your best project"}</p>
            </div>
          </div>

          <div className="surface p-5 sm:p-6">
            <h2 className="text-xl font-black text-slate-950 dark:text-white">Quick Practice</h2>
            <div className="mt-5 space-y-3">
              <button onClick={() => navigate("/coding")} className="w-full rounded-lg bg-emerald-600 px-4 py-3 text-sm font-black text-white transition-all hover:bg-emerald-700 flex items-center justify-center gap-2">
                <Code2 size={18} /> Open Coding Platform
              </button>
              <button onClick={() => navigate("/exams")} className="w-full rounded-lg bg-slate-950 px-4 py-3 text-sm font-black text-white transition-all hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200 flex items-center justify-center gap-2">
                <Target size={18} /> Start Aptitude Tests
              </button>
            </div>
            <div className="mt-6 rounded-lg bg-amber-50 p-4 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
              <p className="text-sm font-black">Mock interview tip</p>
              <p className="mt-1 text-sm">Practice one HR answer and one project explanation daily.</p>
            </div>
          </div>
        </section>

        <section className="surface p-5 sm:p-6">
          <div className="mb-5 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h2 className="text-xl font-black text-slate-950 dark:text-white">Interview Tracker</h2>
              <p className="text-sm text-slate-500 dark:text-slate-400">Track applications, mock rounds, interviews, and offers.</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-4">
              <input className="form-control sm:col-span-1" placeholder="Company" value={newInterview.company} onChange={e => setNewInterview(i => ({ ...i, company: e.target.value }))} />
              <input className="form-control sm:col-span-1" placeholder="Role" value={newInterview.role} onChange={e => setNewInterview(i => ({ ...i, role: e.target.value }))} />
              <select className="form-control sm:col-span-1" value={newInterview.status} onChange={e => setNewInterview(i => ({ ...i, status: e.target.value }))}>
                <option>Applied</option>
                <option>Mock Scheduled</option>
                <option>Interview</option>
                <option>Offer</option>
              </select>
              <button onClick={addInterview} className="btn-primary sm:col-span-1"><Plus size={16} /> Add</button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="text-sm">
              <thead>
                <tr className="text-left text-xs font-black uppercase tracking-widest text-slate-400">
                  <th className="py-3 pr-4">Company</th>
                  <th className="py-3 pr-4">Role</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {interviews.map((item, index) => (
                  <tr key={`${item.company}-${index}`}>
                    <td className="py-4 pr-4 font-bold text-slate-900 dark:text-white">{item.company}</td>
                    <td className="py-4 pr-4 text-slate-600 dark:text-slate-300">{item.role}</td>
                    <td className="py-4 pr-4">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-xs font-black text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                        <CheckCircle2 size={13} /> {item.status}
                      </span>
                    </td>
                    <td className="py-4 pr-4">
                      <button onClick={() => setInterviews(prev => prev.filter((_, i) => i !== index))} className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </div>
  );
}
